import { alpha, debounce, Stack, styled, Switch } from "@mui/material";
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Unstable_Grid2';
import { useContext, useEffect, useState } from 'react';
import { errorMessages } from '../../consts/errorMsgs.js';
import { OverallDataContext } from '../../pages/CalculatorPage.js';
import FormInputPrompt from '../popups/FormInputPrompt.js';
import AddressField from "./AddressField.js";
import ButtonSelectField from "./ButtonSelectField.js";
import DatePickerField from './DatePickerField.js';
import GroupedFields from "./GroupedFields.js";
import InputNumField from './InputNumField.js';
import LimitSumField from "./LimitSumField.js";
import RadioSelectField from "./RadioSelectField.js";
import SelectField from './SelectField.js';

const StyledSwitch = styled(Switch)(({ theme, color }) => ({
    '& .MuiSwitch-switchBase.Mui-checked': {
      color: color,
      '&:hover': {
        backgroundColor: alpha(color, theme.palette.action.hoverOpacity),
      },
    },
    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
      backgroundColor: color,
    },
}));

export default function FormInputField({ field, category, onChangeHandler }) {
    const inputReadOnly = useContext(OverallDataContext).inputReadOnly[0];
    const placeholderData = useContext(OverallDataContext).placeholderData;
    const calcStage = useContext(OverallDataContext).calcStage[0];

    // For input errors
    const setErrors = useContext(OverallDataContext).errorData[1];
    const [errorMsg, setErrorMsg] = useState("");

    const {
        // Question details
        id_name = null,
        prelabel = '',
        label = '',
        caption = '',
        description = '',
        units = '',
        default_val = null,
        options = [],
        groupedOptions = [],
        step = 1,
        maxValue = null,
        sumLimit = 100,
        counterId = '',
        isRequired = false,
        dependentOn = null,
        adrType = null, // for isAddress types

        // To determine type of input
        isText = false,
        isAddress = false,
        isButtonSelect = false,
        isDatePicker = false,
        isGrouped = false,
        isInteger = false,
        isRadio = false,
        isSelect = false,
        isSumLimit = false,
        isToggleable = false,
    } = field ?? {};
    
    const isReadOnly = ( calcStage===2 && category==='global' ) || inputReadOnly;

    const [isInFocus, setFocus] = useState(false);
    const [isToggled, setToggled] = useState(!isToggleable);

    const handleSwitchChange = (newValue) => {
        setToggled(newValue);

        // If field is toggled off, the value for that field needs to be zeroed
        if (!newValue) {
            const fieldData = {
                name: id_name,
                value: 0,
            }
            onChangeHandler(fieldData);
        }
    }

    const regexPattern = /^((>)?[0-9]\d*|[0-9]\d*((\.|-)\d+)?)$/g;
    const checkInputValue = (field_id, field_value) => {
        const fieldData = { name: field_id, value: field_value };
        let errorType = null;
        
        const isEmpty = field_value === "" || (Array.isArray(field_value) && field_value.some(v => !v));

        // 1. Required field check
        if (isRequired && isEmpty) {
            setErrorMsg(errorMessages["RequiredValue"])
            errorType = "RequiredValue";
        }
        // 2. Date range check (only if it's a date picker input)
        else if (isDatePicker) { // Pass only if input is a non-null date range AND end date > start date
            const isValidArray = Array.isArray(field_value) && field_value.length === 2;
            const [startDate, endDate] = isValidArray ? field_value : [null, null];

            const validRange = isValidArray && startDate && endDate && new Date(endDate) > new Date(startDate);

            if (!validRange) {
                setErrorMsg(errorMessages["DateRangePicker"])
                errorType = "DateRangePicker";
            }
        }
        // 3. Max numeric value check
        else if (maxValue !== null && !isNaN(field_value) && parseFloat(field_value) > maxValue) { // Fail if input exceeds a given maxValue
            setErrorMsg(`This value is too large. The max value for this field is ${maxValue.toString()}.`)
            errorType = "ExceedValue";
        }
        // 4. Non-numeric format check (skip if it's select/text/date)
        else if (!(isText || isSelect || isDatePicker) && !isEmpty && !regexPattern.test(field_value)) {
            setErrorMsg(errorMessages["NonNumeric"])
            errorType = "NonNumeric";
        }

        // --- Result handling ---
        if (errorType) {
            setErrors(prev => ({ ...prev, [field_id]: errorType }));
        } else {
            setErrorMsg("");
            debounce(onChangeHandler(fieldData), 4000);

            setErrors(prev => {
                const { [field_id]: _, ...rest } = prev;
                return rest;
            });
        }
    }
    
    const commonProps = {
        checkInputValue,
        counterId,
        default_val,
        errorMsg,
        groupedOptions,
        id_name,
        isRequired,
        isReadOnly,
        onChangeHandler,
        options,
        maxValue,
        setFocus,
        step,
        sumLimit,
        isInteger,
        category,
        units,
        dependentOn,
        adrType
    };
    // Filter out undefined or irrelevant props for this specific component
    const filteredProps = Object.fromEntries(
        Object.entries(commonProps).filter(([_, value]) => value !== undefined)
    );
      
    // Define mapping of flags to components
    const componentMap = [
        { condition: isSelect, component: SelectField },
        { condition: isButtonSelect, component: ButtonSelectField },
        { condition: isRadio, component: RadioSelectField },
        { condition: isDatePicker, component: DatePickerField },
        { condition: isAddress, component: AddressField },
        { condition: isSumLimit, component: LimitSumField },
        { condition: isGrouped, component: GroupedFields },
    ];
    // Find the first matching component
    const InputComponent = componentMap.find(c => c.condition)?.component || InputNumField;

    useEffect(()=>{
        if (calcStage===2 && placeholderData?.[category]?.[id_name]) setToggled(true);
    }, [calcStage, placeholderData, id_name]);

    return (
        <Box
            className="form-field" 
            borderLeft={isInFocus ? '4px solid var(--pri-yellow)' : '4px solid white'}
        >
            <Grid container spacing={1}>
                <Grid 
                    xs={12} 
                    sx={{ display: 'flex', justifyContent: 'space-between'}}
                >
                    {
                        (!isToggleable || isToggled) ?
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
                            <label htmlFor={id_name} className={ isRequired ? "required_input" : "" }>
                                { label }
                            </label>
                            { caption && 
                                <div>
                                    <Typography color="text.secondary" variant='caption'>{ caption }</Typography>
                                </div>
                            }
                        </Box>
                        :
                        <label>
                            { prelabel }
                        </label>
                    }
                    <Stack direction={{ xs: "column", sm: "row"}} alignItems={{ xs: "end", sm: "center"}} spacing={1} sx={{ display: 'flex' }}>
                        <FormInputPrompt id_name={id_name} content={description} />
                        { isToggleable &&
                            <Switch
                            checked={isToggled}
                            onChange={(e,v)=>handleSwitchChange(v)}
                            inputProps={{ 'aria-label': 'controlled' }} 
                            // color='#1F4E79'
                        />
                        }
                    </Stack>
                </Grid>
                    
                {
                    isToggled &&
                    <>
                    <Grid 
                        xs={true} 
                    >
                        <InputComponent {...filteredProps} />
                    </Grid>
                    {
                        (units && !isSumLimit && !isGrouped) &&
                        <Grid 
                            xs={units ? 'auto' : 0} 
                            className='vertically-centered' 
                            sx={{overflow: 'clip'}}
                        >
                            <Typography color="text.secondary">{ units }</Typography>
                        </Grid>
                    }
                    </>
                }
            </Grid>
        </Box>
    )
}